import { createRef, RefObject, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { CForm } from '@coreui/react-pro'
import Button from 'components/commons/button/Button'
import FormInput from 'components/commons/forms/FormInput'
import Validator from 'components/commons/forms/Validator'
import Tag from 'components/commons/label/Tag'
import toaster from 'components/commons/toast/Toast'
import FormLayout from 'layout/FormLayout'
import { fetchSettingPassword } from 'redux/action/api'
import { ERROR_DETAIL_CODE } from 'utils/constants/statusCode'
import { validateHelper } from 'utils/helpers'
import { useAppDispatch } from 'utils/hooks'

const SettingPassword = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const token = params.get('token')

  const [password, setPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')

  const passwordValidatorRef = createRef<IValidatorComponentHandle>()
  const confirmPasswordValidatorRef = createRef<IValidatorComponentHandle>()

  const isConfirmPasswordMatched = () =>
    password.trim() === confirmPassword.trim()

  const isPassword = (
    password: string,
    ref: RefObject<IValidatorComponentHandle>,
  ) => {
    if (validateHelper.isEmpty(password)) {
      ref?.current?.onValidateMessage('message.require')
      return false
    }

    if (!validateHelper.isPassword(password.trim())) {
      ref?.current?.onValidateMessage(
        'message.invalid_password_registration_user',
      )
      return false
    }

    ref?.current?.onValidateMessage('')
    return true
  }

  const isValidate = () => {
    let isValid = true

    isValid = isPassword(password, passwordValidatorRef)
    isValid = isPassword(confirmPassword, confirmPasswordValidatorRef)

    if (!isConfirmPasswordMatched()) {
      confirmPasswordValidatorRef?.current?.onValidateMessage(
        'message.password_not_match',
      )
      isValid = false
    }

    return isValid
  }

  const handleSubmit = () => {
    if (!token) {
      toaster.error({ message: t('message.token_invalid') })
      return
    }
    if (!isValidate()) {
      return
    }
    passwordValidatorRef?.current?.onValidateMessage('')
    confirmPasswordValidatorRef?.current?.onValidateMessage('')

    dispatch(
      fetchSettingPassword({
        isLoading: true,
        params: {
          verifyToken: token,
          password: password.trim(),
        },
        onSuccess: () => {
          toaster.success({ message: t('message.change_password_success') })
          navigate('/login')
        },
        onError: (error: IErrorAPIRes) => {
          switch (error?.code) {
            case ERROR_DETAIL_CODE.USER_NOT_FOUND:
              toaster.error({ message: t('message.user_not_found') })
              break
            case ERROR_DETAIL_CODE.TOKEN_INVALID:
              toaster.error({ message: t('message.token_invalid') })
              break
            case ERROR_DETAIL_CODE.TOKEN_EXPIRED:
              toaster.error({ message: t('message.token_expired') })
              break
            case ERROR_DETAIL_CODE.USER_INACTIVE:
              toaster.error({ message: t('message.user_inactive') })
              break
            default:
              toaster.error({ message: t('error.something_wrong') })
              break
          }
        },
      }),
    )
  }

  return (
    <FormLayout>
      <CForm className="form__background">
        <span className="form__title">{t('setting_password.title')}</span>
        <div className="form__body">
          <label>
            <span className="d-flex align-items-center bases__gap10 bases__margin--bottom12">
              <Tag isRequired />
              <span>{t('base.password')}</span>
            </span>
            <Validator ref={passwordValidatorRef}>
              <FormInput
                isPassword
                value={password}
                onChange={(value: string) => setPassword(value)}
                required
                placeholder={
                  t('setting_password.placeholder.please_input_password')!
                }
                className="mcs-input--medium bases__margin--bottom8"
                maxLength={32}
                autoFocus
              />
            </Validator>
            <span className="bases__font--12">
              {t('setting.user.enter_8_digits')}
            </span>
          </label>
          <label>
            <span className="d-flex align-items-center bases__gap10 bases__margin--bottom12">
              <Tag isRequired />
              <span>{t('setting_password.confirm_password')}</span>
            </span>
            <Validator ref={confirmPasswordValidatorRef}>
              <FormInput
                isPassword
                value={confirmPassword}
                onChange={(value: string) => setConfirmPassword(value)}
                required
                placeholder={
                  t('setting_password.placeholder.please_input_password')!
                }
                className="mcs-input--medium bases__margin--bottom8"
                maxLength={32}
              />
            </Validator>
            <span className="bases__font--12">
              {t('setting.user.enter_8_digits')}
            </span>
          </label>
        </div>
        <Button
          className="w-280"
          shape="rounded-pill"
          size="large"
          onClick={handleSubmit}
        >
          {t('csv_input.button_upload')}
        </Button>
      </CForm>
    </FormLayout>
  )
}

export default SettingPassword
